import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FlightShopSelectors,
  useSelectDepartureFlight,
} from "@b2bportal/core-flights";
import type {
  AppDispatch,
  FlightFareSelectionFunction,
} from "@b2bportal/core-types";
import { PackageNavigationFlow, PackageSteps } from "@b2bportal/core-utilities";
import { useDeparturePageContainer } from "@tenant/flights";
import { getPricingMode } from "@/store/flights/shop/store";
import { PackageFlightSelectors } from "@/store/packages/selectors";
import { PackagesBaseActions } from "@/store/packages/slice/packages";

export const usePackageFlightDeparturePageContainer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const lastSelectedFareIdRef = useRef<string | undefined>();

  const pricingMode = useSelector(getPricingMode);

  const packageStateByFareId = useSelector(
    PackageFlightSelectors.getPackageStateByFareId
  );

  const originalHandleFareSelect = useSelectDepartureFlight();

  // Wrap handleFareSelect to capture the fareId
  const handleFareSelect: FlightFareSelectionFunction = useCallback(
    (params) => {
      lastSelectedFareIdRef.current = params.fareId;
      return originalHandleFareSelect(params);
    },
    [originalHandleFareSelect]
  );

  const originalGoToNextStep = PackageNavigationFlow.useGoToNextStep(
    PackageSteps.FLIGHT_DEPARTURE
  );

  const fareDetails = useSelector(
    FlightShopSelectors.getSelectedTripDetailsAndFareDetails
  );

  const goToNextStep = useCallback(() => {
    const fareId = lastSelectedFareIdRef.current;

    // Update selected package with latest info based on fareId
    if (fareId && packageStateByFareId) {
      const updatedPackage = packageStateByFareId[fareId];
      if (updatedPackage) {
        dispatch(PackagesBaseActions.setCurrentActivePackage(updatedPackage));
      }
    }

    // Extract and store cancellation policy for the selected flight
    // The policy comes from: fareDetails.fareDetails[0].slices[0].amenitiesUtas.utas.cancellationPolicy
    const cancellationPolicy =
      fareDetails?.fareDetails?.[0]?.slices?.[0]?.amenitiesUtas?.utas
        ?.cancellationPolicy;

    if (cancellationPolicy) {
      dispatch(
        PackagesBaseActions.setSelectedFlightCancellationPolicy(
          cancellationPolicy
        )
      );
    }

    // Then proceed to next step
    originalGoToNextStep();
  }, [dispatch, packageStateByFareId, originalGoToNextStep, fareDetails]);

  const onErrorReload = PackageNavigationFlow.useGoToNextStep(
    PackageSteps.HOTEL_SHOP
  );

  // Get package-aware filtered flight list instead of core filtered list
  const packageFilteredFlightList = useSelector(
    PackageFlightSelectors.getPackageFilteredAndSortedDepartureFlightList
  );

  const containerProps = useDeparturePageContainer({
    goToNextStep,
    onErrorReload,
    pricingMode,
  });

  // Override handleFareSelect and filtered flight list with package-aware versions
  return {
    ...containerProps,
    handlers: {
      ...containerProps.handlers,
      handleFareSelect,
    },
    context: {
      ...containerProps.context,
      handleFareSelect,
      // Override flight list with package-filtered version
      flightList: packageFilteredFlightList,
    },
  };
};
